import { tt } from '../i18n';
import { OnScreenKeyboard } from './OnScreenKeyboard';
import { oskEnabled } from './useKeyboard';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import type { CatalogResponse, Features } from '@dastarhan/contracts';
import type { ItemModifier, OrderMode } from '@dastarhan/core';
import { ModifierDialog } from './ModifierDialog';
import { formatMoney, lineSum, orderTotals, type OrderState, rankProducts, branchIds } from '@dastarhan/core';

export function OrderScreen({ order, catalog, cashier, guestName, depositNote, shiftSum, toast, onToastUndo, shiftHours, twinTab, keysHelp, onKeysHelpClose, online, queued, onAdd, onLineTap, onLineBump, onLineDown, onCourse, onLineRemove, onNote, onNoteAll, onRepeat, onQueuedTap, onPay, onCloseShift, onExit, onChecks, onMode, table, onPickTable, onShowMap, onTransfer, onReassign, canReassign, onVoidPick, canVoid, needsPinToVoid, onFire, onOpenTables, onCard, freshCount, totals, onPrinter, features, waiter, senior, parkedCount, canPark, onPark, onPrecheck, precheckStale, stopCount, onStopList, extra }: {
  order: OrderState;
  catalog: CatalogResponse;
  cashier: string;
  /** выбранный гость — видно не только на пречеке */
  guestName?: string | null;
  /** депозит выбранной брони — зачтётся в оплате */
  depositNote?: number | null;
  /** выручка смены нарастающим итогом */
  shiftSum?: number;
  /** короткая подсказка внизу экрана (обратимость действия) */
  toast?: string | null;
  /** «Вернуть» прямо в тосте: у планшета нет Ctrl+Z */
  onToastUndo?: (() => void) | null;
  /** сколько часов идёт смена — предупреждение о забытом закрытии */
  shiftHours?: number | null;
  /** касса открыта в двух вкладках — риск двойных номеров */
  twinTab?: boolean;
  /** шпаргалка горячих клавиш поверх экрана (Ctrl+/) */
  keysHelp?: boolean;
  onKeysHelpClose?: () => void;
  online: boolean;
  queued: number;
  onAdd: (p: CatalogResponse['products'][number], mods: ItemModifier[]) => void;
  onLineTap: (itemId: string) => void;
  /** быстрый добор: двойной тап по строке — +1 к количеству */
  onLineBump: (itemId: string) => void;
  onLineDown: (itemId: string) => void;
  onCourse: (itemId: string) => void;
  /** долгий тап по строке — снять позицию (обратимо через Ctrl+Z) */
  onLineRemove: (itemId: string) => void;
  /** заметка кухне: «без соли» — печатается на бегунке и видна в KDS */
  onNote: (itemId: string) => void;
  /** заметка кухне всему заказу разом — «без лука» целиком */
  onNoteAll: () => void;
  /** тап по «не отправлено» — объяснить и дослать */
  onQueuedTap: () => void;
  /** ещё одна такая же — qty+1 одним тапом */
  onRepeat: (itemId: string) => void;
  onPay: () => void;
  onCloseShift: () => void;
  onExit: () => void;
  onChecks: () => void;
  onMode: (mode: OrderMode) => void;
  table: string | null;
  onPickTable: () => void;
  onShowMap: () => void;
  onTransfer: () => void;
  onReassign: () => void;
  /** отказ от блюда: выбрать позицию и снять с причиной */
  onVoidPick: () => void;
  /** право передавать счёт другому сотруднику */
  canReassign: boolean;
  onFire: () => void;
  onOpenTables: () => void;
  onCard: () => void;
  freshCount: number;
  /** итоги со сбором и скидками, посчитанные кассой */
  totals?: ReturnType<typeof orderTotals>;
  onPrinter: () => void;
  features: Features;
  waiter: boolean;
  /** старший меняет отправленное на кухню, официант — нет */
  senior: boolean;
  /** снимать позицию можно — прямо или с PIN старшего */
  canVoid: boolean;
  /** снятие потребует PIN старшего: предупреждаем заранее */
  needsPinToVoid: boolean;
  parkedCount: number;
  canPark: boolean;
  onPark: () => void;
  onPrecheck: () => void;
  /** после печати пречека заказ изменился — счёт пора перепечатать */
  precheckStale?: boolean;
  /** сколько позиций на стопе — чип у поиска греется */
  stopCount?: number;
  /** стоп-лист в одно касание — раньше прятался в настройках печати */
  onStopList?: () => void;
  extra?: ReactNode;
}) {
  const longPress = useRef<number | null>(null);
  const longFired = useRef(false);
  // Уровень меню залипает: обед — это сто заказов из «Горячего»
  // подряд, и касса открывается там же, где её оставили
  const [cat, setCatRaw] = useState<string | null>(
    () => localStorage.getItem('dastarhan.lastCat') || null,
  );
  const setCat = (v: string | null) => {
    setCatRaw(v);
    if (v) localStorage.setItem('dastarhan.lastCat', v);
    else localStorage.removeItem('dastarhan.lastCat');
  };
  const [modFor, setModFor] = useState<CatalogResponse['products'][number] | null>(null);
  // Тап по строке раскрывает её действия, а не снимает позицию:
  // случайное касание в час пик не должно ломать чек
  const [openLine, setOpenLine] = useState<string | null>(null);
  // Итоги приходят готовыми: сбор и скидки считает касса,
  // и второй расчёт здесь означал бы два разных ответа
  const t = totals ?? orderTotals(order);
  const live = order.items.filter((i) => !i.removed);

  // В зале стол обязателен: без него заказ некуда отнести, а в отчёте
  // он повиснет ничьим. На вынос и доставку стол не нужен
  const needTable = features.tables && order.mode === 'DINE_IN' && !table;

  // Поиск важнее категорий: официант помнит название и печатает на бегу.
  // Пока строка не пуста, ищем по ВСЕМУ меню — иначе блюдо из другой
  // категории не найдётся, и человек решит, что его нет
  const [q, setQ] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  // Экранная клавиатура: на Windows-планшете системная не всплывает,
  // а на кассовом терминале физической может не быть вовсе.
  // В exe включена из коробки, в браузере — по настройке устройства
  const oskOn = oskEnabled();
  const [oskOpen, setOskOpen] = useState(false);
  /*
   * Касса ходит вглубь, как привык персонал заведений: дом показывает
   * группы, тап по группе — её подгруппы, тап по подгруппе — товары.
   * Подгрупп нет — остаются только товары уровня, тупика не бывает;
   * товары, лежащие в самой группе рядом с подгруппами, идут после
   * плиток подгрупп. Поиск сквозной и уровней не знает: официант
   * помнит блюдо, а не путь к нему.
   */
  const byIdCat = useMemo(
    () => new Map(catalog.categories.map((c) => [c.id, c])),
    [catalog.categories],
  );
  // Сохранённый уровень мог исчезнуть (категорию убрали) — тогда дом
  const level = cat && byIdCat.has(cat) ? cat : null;
  const cur = level ? byIdCat.get(level)! : null;
  const up = cur?.parentId ? byIdCat.get(cur.parentId) ?? null : null;
  const subs = useMemo(() => catalog.categories.filter((c) => (
    level
      ? c.parentId === level
      // дом: группы верхнего уровня; осиротевшие подгруппы поднимаются
      : (c.parentId === null || !byIdCat.has(c.parentId))
  )), [catalog.categories, level, byIdCat]);
  const goods = useMemo(() => {
    if (q.trim()) return rankProducts(catalog.products, q);
    return catalog.products.filter((p) => (p.categoryId ?? null) === level);
  }, [catalog.products, level, q]);

  return (
    <div className="work">
      <aside className="ticket">
        <header>
          <b>Чек №{order.number}</b>
          <span className="who">
            <OrderAge openedAt={order.openedAt} />
            {cashier}{guestName ? <small className="order-age"> · ♥ {guestName}</small> : null}
            <small className="where"> · {localStorage.getItem('dastarhan.place') ?? ''}</small>
            {twinTab && (
              <small className="where stale-shift"> · касса открыта в двух вкладках — закройте лишнюю</small>
            )}
            {(shiftHours ?? 0) >= 14 && (
              <small className="where stale-chip" role="button"
                onClick={() => window.dispatchEvent(
                  new CustomEvent('dastarhan:open-shift-sheet'))}>
                {' '}· смена {shiftHours} ч — закрыть?</small>
            )}
            {(shiftSum ?? 0) > 0 && (
              <small className="where"> · смена {formatMoney(shiftSum!)}</small>
            )}
          </span>
        </header>
        <div className="mode-switch" role="radiogroup" aria-label="Режим заказа">
          {([['DINE_IN', 'Зал'], ['TAKEAWAY', 'С собой'], ['DELIVERY', 'Доставка']] as const)
            .filter(([m]) => features.tables || m !== 'DINE_IN')
            .map(([m, label]) => (
            <button key={m} className={order.mode === m ? 'on' : ''}
              role="radio" aria-checked={order.mode === m}
              onClick={() => onMode(m)}>{label}</button>
          ))}
        </div>
        {features.tables && order.mode === 'DINE_IN' && (
          <>
            <button className="table-pick" onClick={onPickTable}>
              {table ? <>{tt('table')}: <b>{table}</b></> : tt('chooseTable')}
            </button>
            {/* Инструменты зала — подписаны словами, а не загаданы значками.
                Перенос не прячется, а гаснет: место кнопки постоянно,
                и официант знает, где она будет, когда понадобится */}
            <div className="hall-tools" role="group" aria-label="Зал">
              <button onClick={onShowMap} title="Кто зовёт, где ждут счёт, куда посадить">
                <i aria-hidden>▦</i>Схема зала
              </button>
              <button onClick={onOpenTables} title="Все открытые заказы: свои и чужие">
                <i aria-hidden>☰</i>Открытые
              </button>
              <button onClick={onCard} title="Скидочная карта или купон — к сканеру">
                <i aria-hidden>▤</i>Карта гостя
              </button>
              <button onClick={onTransfer} disabled={!table || live.length === 0}
                title={!table || live.length === 0
                  ? 'Переносить пока нечего: нужен стол и позиции в чеке'
                  : 'Перенести заказ или блюда на другой стол'}>
                <i aria-hidden>⇄</i>Перенос
              </button>
              {/* Передача стола видна только тому, кто вправе: официанту
                  без права кнопка не нужна, а место в сетке дорого */}
              {canReassign && (
                <button onClick={onReassign} disabled={!table || live.length === 0}
                  title={!table || live.length === 0
                    ? 'Передавать пока нечего: нужен стол с заказом'
                    : 'Передать стол сменщику вместе с выручкой'}>
                  <i aria-hidden>⇉</i>Передать
                </button>
              )}
              {/* Отказ от блюда: снять позицию с причиной. Показываем
                  и тому, кому положено спросить старшего: «по PIN» —
                  это «можно с разрешением», а не «нельзя». Раньше
                  кнопки у официанта не было вовсе, и отказ упирался
                  в невидимую стену (жалоба Дыма, 10.08) */}
              {canVoid && (
                <button onClick={onVoidPick} disabled={live.length === 0}
                  title={live.length === 0
                    ? 'В чеке нет позиций'
                    : needsPinToVoid
                      ? 'Снять блюдо — спросит PIN старшего'
                      : 'Снять блюдо с указанием причины'}>
                  <i aria-hidden>✕</i>Отказ
                </button>
              )}
            </div>
          </>
        )}
        <div className="lines">
          {live.length === 0 && (
            <p className="empty">Нажимайте на блюда справа — они лягут в чек. Тап по строке откроет её действия: заметка кухне, убрать позицию.</p>
          )}
          {live.map((i) => (
            <div key={i.itemId}
              className={`line ${openLine === i.itemId ? 'open' : ''} ${i.fired ? 'fired' : ''}`}
              role="button" tabIndex={0}
              onPointerDown={() => {
                // 600 мс удержания — снять позицию сразу (для тех, кто привык);
                // отмена движением или отпусканием. Снятое возвращается
                // кнопкой «Вернуть» в тосте
                longPress.current = window.setTimeout(() => {
                  longPress.current = null;
                  longFired.current = true;
                  onLineRemove(i.itemId);
                }, 600);
              }}
              onPointerUp={() => {
                if (longPress.current) { window.clearTimeout(longPress.current); longPress.current = null; }
              }}
              onPointerLeave={() => {
                if (longPress.current) { window.clearTimeout(longPress.current); longPress.current = null; }
              }}
              onClick={(e) => {
                // долгий тап уже отработал — клик по нему не раскрывает строку
                if (longFired.current) { longFired.current = false; return; }
                // двойной тап — добор; одиночный ждёт 250 мс, чтобы не мигать
                if (e.detail >= 2) { onLineBump(i.itemId); return; }
                const id = window.setTimeout(
                  () => setOpenLine((cur) => (cur === i.itemId ? null : i.itemId)), 250);
                const cancel = () => { window.clearTimeout(id); window.removeEventListener('dblclick', cancel, true); };
                window.addEventListener('dblclick', cancel, true);
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') setOpenLine((cur) => (cur === i.itemId ? null : i.itemId)); }}
              aria-label={`Позиция ${i.name}: действия`}>
              <span className="name">{i.name}</span>
                            {/* Крестик убавляет по одной порции и снимает строку
                  целиком с последней: гость передумал — официант тычет
                  крестик сколько нужно, число тает, строка исчезает.
                  До отправки это чистое снятие; после — на кухне
                  готовят, и правит только старший, крестик гаснет */}
              <button className="note-btn line-remove"
                title={i.qty > 1 ? 'Убрать одну' : 'Убрать из чека'}
                disabled={i.fired && !senior}
                onClick={(e) => { e.stopPropagation(); onLineDown(i.itemId); }}>✕</button>
              {features.courses && (
                <button className="note-btn course-btn"
                  title="Курс подачи: когда нести"
                  onClick={(e) => { e.stopPropagation(); onCourse(i.itemId); }}>
                  {i.course === 0 ? '!' : String(i.course ?? 1)}
                </button>
              )}
              <span className="sum money">{formatMoney(lineSum(i))}</span>
              <span className="qty">{i.qty} × <span className="money">{formatMoney(i.unitPrice)}</span></span>
              {i.modifiers.length > 0 && (
                <span className="mods">{i.modifiers.map((m) => m.name).join(' · ')}</span>
              )}
              {/* Редкие действия раскрываются тапом и названы словами:
                  снятие позиции больше не живёт на случайном касании */}
              {openLine === i.itemId && (
                <div className="line-actions">
                  {/* Отправленной строке кнопки гаснут честно: живая
                      кнопка, которая молча отказывает тостом, читается
                      как «клавиатура сломалась». Причина — в подсказке */}
                  <button className="btn"
                    disabled={i.fired && !senior}
                    title={i.fired && !senior
                      ? 'Бегунок уже на кухне — повар его прочитал'
                      : undefined}
                    onClick={(e) => {
                      e.stopPropagation(); setOpenLine(null); onNote(i.itemId);
                    }}>✎ Заметка кухне</button>
                  <button className="btn danger"
                    disabled={i.fired && !senior}
                    title={i.fired && !senior
                      ? 'Блюдо уже готовят — снять может только старший'
                      : undefined}
                    onClick={(e) => {
                      e.stopPropagation(); setOpenLine(null); onLineTap(i.itemId);
                    }}>Убрать из чека</button>
                </div>
              )}
            </div>
          ))}
        </div>
        {features.tables && (
          <div className="park-row">
            <button className="btn" title="Заметка кухне всему заказу"
              onClick={onNoteAll}>✎ Кухне</button>
            <button className="btn" disabled={!canPark} onClick={onPrecheck}
              title={precheckStale ? 'Состав изменился после печати — перепечатайте счёт' : undefined}>
              Счёт{precheckStale ? ' ⚠' : ''}
            </button>
            {!waiter && (
              <button className="btn" disabled={!canPark} onClick={onPark}>
                {tt('hold')}{parkedCount > 0 ? ` · держим ${parkedCount}` : ''}
              </button>
            )}
          </div>
        )}
        {/* Отправка на кухню. В официантском укладе её делает «Заказать»
            ниже — вторая кнопка на то же действие только путает:
            официант жмёт наугад и не понимает, чем они отличаются */}
        {!waiter && features.kitchen && live.length > 0 && (
          <button className="fire-btn" onClick={onFire} disabled={freshCount === 0}>
            {freshCount > 0 ? `Печать · ${freshCount}` : 'Напечатано'}
          </button>
        )}
        {waiter ? (
          /* Официант не видит оплаты вовсе: деньги принимает кассир.
             Ему нужны два действия — оставить стол открытым для дозаказа
             и закрыть счёт, чтобы напечатать чек гостю */
          <div className="waiter-actions">
            {/* Число на кнопке — сколько строк уйдёт на кухню. Ноль
                значит «всё уже там», и кнопка гаснет: повторное нажатие
                не должно готовить блюдо дважды */}
            <button className="btn waiter-order"
              disabled={!canPark || needTable || freshCount === 0}
              title={needTable ? 'Сначала выберите стол'
                : freshCount === 0 ? 'Всё уже на кухне'
                : 'Отправить на кухню, стол останется открытым'}
              onClick={onFire}>
              {freshCount > 0 ? `Заказать · ${freshCount}` : 'Всё на кухне'}
            </button>
            <button className="btn waiter-order-exit"
              disabled={!canPark || needTable}
              title={needTable ? 'Сначала выберите стол'
                : 'Отправить, отложить стол и выйти — следующий введёт свой PIN'}
              onClick={onPark}>
              Заказать и выйти
            </button>
            <button className="total-board waiter" disabled={!canPark || needTable}
              onClick={onPrecheck}>
              <span className="label">Закрыть счёт</span>
              <span className="money">
                {formatMoney(t.total)}
                {t.discount > 0 && <small className="disc-note"> −{formatMoney(t.discount)}</small>}
                {'service' in t && (t.service ?? 0) > 0 && (
                  <small className="disc-note"> · обсл. {t.servicePct}%</small>
                )}
              </span>
            </button>
          </div>
        ) : (
        <button className={`total-board mode-${order.mode.toLowerCase()}`}
          disabled={t.total === 0 || needTable}
          title={needTable ? 'Сначала выберите стол' : undefined}
          onClick={onPay}>
          <span className="label">{tt('confirm')}</span>
          <span className="money">
            {formatMoney(t.total)}
            {t.discount > 0 && <small className="disc-note"> −{formatMoney(t.discount)}</small>}
            {'service' in t && (t.service ?? 0) > 0 && (
              <small className="disc-note"> · обсл. {t.servicePct}%</small>
            )}
            {depositNote ? <small className="disc-note"> · деп. {formatMoney(depositNote)}</small> : null}
          </span>
        </button>
        )}
        <div className="statusbar">
          <i className={`dot ${online ? '' : 'off'}`} aria-hidden />
          <span>{online ? 'В сети' : 'Офлайн — чеки в очереди'}</span>
          {queued > 0 && (
            <button className="queued-btn" title="Что это значит и как дослать"
              onClick={onQueuedTap}>· не отправлено: {queued} ↻</button>
          )}
          <span className="spacer" />
          <span>{t.positions} поз.</span>
          {/* Сменные операции и настройки — одно окно с человеческим
              именем вместо безымянной шестерёнки */}
          <button className="btn ghost"
            onClick={onPrinter} aria-label="Смена и настройки">⚙ Ещё</button>
          {!waiter && (
            <button className="btn ghost"
              onClick={onChecks}>
              Чеки{order.number > 1 ? ` · ${order.number - 1}` : ''}
            </button>
          )}
          <button className="btn ghost"
            onClick={onCloseShift}>Смена</button>
          {/* Выход из-под своего PIN: официант сдал стойку сменщику.
              Смена при этом НЕ закрывается — она общая на терминал,
              а закрывает её тот, кто считает кассу вечером */}
          <button className="btn ghost exit-btn"
            onClick={onExit} title={`Выйти из-под ${cashier}`}>Выйти</button>
        </div>
      </aside>

      <section className="catalog">
        <div className="search-row">
          <input className="good-search" placeholder="Найти блюдо"
            autoCorrect="off" autoComplete="off"
            ref={searchRef}
            value={q} onChange={(e) => setQ(e.target.value)}
            onFocus={() => { if (oskOn) setOskOpen(true); }}
            autoCapitalize="off" spellCheck={false} />
          {q && (
            <button className="search-clear" onClick={() => setQ('')}
              title="Очистить поиск">×</button>
          )}
          {/* Стоп-лист там, где он нужен, — рядом с меню, а не в настройках
              печати: «казы кончился» случается у плиты, а не у принтера */}
          {onStopList && (
            <button className={`stop-chip ${(stopCount ?? 0) > 0 ? 'hot' : ''}`}
              onClick={onStopList} title="Стоп-лист точки: что закончилось">
              Стоп{(stopCount ?? 0) > 0 ? ` · ${stopCount}` : ''}
            </button>
          )}
        </div>

        {/* Хлебные крошки: дом / группа / подгруппа. Официант всегда
            видит, где стоит; тап по дому — к группам, тап по текущей —
            на уровень вверх. Во время поиска путь гаснет: ищем по всему */}
        <nav className="cats crumbs" aria-label="Путь по меню"
          style={q ? { opacity: 0.4, pointerEvents: 'none' } : undefined}>
          <button className={`cat crumb-home ${level === null ? 'on' : ''}`}
            onClick={() => setCat(null)} title="К группам меню">⌂</button>
          {up && (
            <>
              <span className="crumb-sep" aria-hidden>/</span>
              <button className="cat"
                style={{ borderLeftColor: up.color ?? 'var(--line)' }}
                onClick={() => setCat(up.id)}>{up.name}</button>
            </>
          )}
          {cur && (
            <>
              <span className="crumb-sep" aria-hidden>/</span>
              <button className="cat on"
                style={{ borderLeftColor: cur.color ?? 'var(--line)' }}
                onClick={() => setCat(cur.parentId ?? null)}>{cur.name}</button>
            </>
          )}
        </nav>
        {/* Без стола заказ некуда отнести: в зале это не «пока не выбрал»,
            а «ещё не начал». Молчаливый disabled оставил бы официанта
            гадать, почему блюда не нажимаются */}
        {needTable && (
          <button className="need-table" onClick={onPickTable}>
            <b>Сначала выберите стол</b>
            <span>Заказ в зале всегда принадлежит столу — нажмите, чтобы выбрать</span>
          </button>
        )}

        <div className="goods">
          {catalog.products.length === 0 && (
            <p className="hint" style={{ gridColumn: '1 / -1' }}>
              Меню пустое. Заведите блюда в офисе — они появятся здесь через минуту.
            </p>
          )}
          {catalog.products.length > 0 && q.trim() !== '' && goods.length === 0 && (
            <p className="hint" style={{ gridColumn: '1 / -1' }}>
              По запросу «{q}» ничего не нашлось
            </p>
          )}
          {catalog.products.length > 0 && !q.trim() && subs.length === 0 && goods.length === 0 && (
            <p className="hint" style={{ gridColumn: '1 / -1' }}>
              В этой группе пока пусто.
            </p>
          )}
          {/* Плитки подгрупп — раньше товаров: сначала строение, потом
              блюда. Счётчик — сколько позиций живёт в ветке */}
          {!q.trim() && subs.map((c) => {
            const ids = new Set(branchIds(c.id, catalog.categories));
            const cnt = catalog.products.filter((p) => p.categoryId && ids.has(p.categoryId)).length;
            return (
              <button key={c.id} className="good cat-tile"
                style={c.color ? { borderTopColor: c.color } : undefined}
                onClick={() => setCat(c.id)}>
                <span className="name">{c.name}</span>
                <span className="cat-open" aria-hidden>›</span>
                <span className="cat-count">{cnt}</span>
              </button>
            );
          })}
          {goods.map((p) => {
            const stop = p.id in catalog.stopList;
            const left = catalog.stopList[p.id];
            return (
              <button key={p.id}
                className={`good ${stop && left === null ? 'stopped' : ''} ${needTable ? 'locked' : ''}`}
                disabled={(stop && left === null) || needTable}
                title={needTable ? 'Сначала выберите стол' : undefined}
                onClick={() => {
                  if (p.modifierGroupIds.length > 0) setModFor(p);
                  else onAdd(p, []);
                  /*
                   * Блюдо выбрано — клавиатура своё отработала и уходит
                   * сама: она нужна для поиска, поиск завершён, а снизу
                   * ей больше нечего заслонять. Строка чистится под
                   * следующий раз; новый поиск — тап по полю, клавиатура
                   * вернётся вместе с фокусом
                   */
                  if (q) setQ('');
                  setOskOpen(false);
                  searchRef.current?.blur();
                }}>
                <span className="name">{p.name}</span>
                <span className="price money">{formatMoney(p.price)}</span>
                {stop && <span className="stop">{left === null ? 'Стоп' : `Осталось ${left}`}</span>}
              </button>
            );
          })}
        </div>
      </section>
      {keysHelp && (
        <div className="dialog-backdrop" role="dialog" aria-modal="true" aria-label="Горячие клавиши"
          onClick={onKeysHelpClose}>
          <div className="dialog" style={{ maxWidth: 420, textAlign: 'left' }}>
            <h1>⌨ Горячие клавиши</h1>
            <p className="hint"><b>Ctrl+Z</b> — вернуть последнее действие</p>
            <p className="hint"><b>Ctrl+P</b> — напечатать счёт</p>
            <p className="hint"><b>Ctrl+Enter</b> — перейти к оплате</p>
            <p className="hint"><b>Esc</b> — назад из оплаты</p>
            <p className="hint"><b>Долгий тап</b> по строке — снять позицию</p>
            <p className="hint"><b>Тап</b> по строке — заметка кухне и снятие</p>
            <p className="hint"><b>Двойной тап</b> по строке — +1 к количеству</p>
            <p className="hint" style={{ marginTop: 10 }}>
              Связь: <b>{online ? 'онлайн' : 'офлайн'}</b>
              {queued > 0 ? ` · в очереди ${queued}` : ' · очередь пуста'}
              {(() => {
                const at = localStorage.getItem('dastarhan.lastSync');
                return at ? ` · последняя отправка ${new Date(at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}` : '';
              })()}
            </p>
            <button className="btn" onClick={onKeysHelpClose}>Закрыть (Ctrl+/)</button>
          </div>
        </div>
      )}
      {toast && (
        <div className="toast" role="status">
          {toast}
          {onToastUndo && <button onClick={onToastUndo}>Вернуть</button>}
        </div>
      )}
      {modFor && (
        <ModifierDialog product={modFor}
          groups={catalog.modifierGroups.filter((g) => modFor.modifierGroupIds.includes(g.id))}
          onCancel={() => setModFor(null)}
          onConfirm={(mods) => {
            onAdd(modFor, mods); setModFor(null);
            // Тот же порядок, что и без модификаторов: блюдо выбрано —
            // клавиатура уходит, строка поиска чистится
            if (q) setQ('');
            setOskOpen(false);
            searchRef.current?.blur();
          }}
        />
      )}
      {extra}
      {oskOpen && (
        <OnScreenKeyboard
          onKey={(ch) => { setQ((v) => v + ch); searchRef.current?.focus(); }}
          onBackspace={() => setQ((v) => v.slice(0, -1))}
          onClear={() => { setQ(''); searchRef.current?.focus(); }}
          onClose={() => setOskOpen(false)} />
      )}
    </div>
  );
}

/** «открыт 12 мин» — возраст заказа тикает раз в минуту */
function OrderAge({ openedAt }: { openedAt: string }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => tick((x) => x + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);
  const min = Math.floor((Date.now() - new Date(openedAt).getTime()) / 60_000);
  if (min < 1) return null;
  return <small className="order-age">открыт {min} мин · </small>;
}
